/* eslint-disable react/prop-types */
import React, { useEffect, useState, useRef } from 'react'
import { Animated, StyleSheet, Image, PanResponder } from 'react-native'
import { getOverlappingCardSlotIndex, getCardSlotData } from '../CardSlots/CardSlots'
import { getTrashCanCoords, isOverlappingTrashCan } from '../TrashCan/TrashCan'
import grab from '../../assets/sounds/cardgrab_01.mp4'
import drop from '../../assets/sounds/card_drop_01.mp4'
import trash from '../../assets/sounds/card_in_trashcan.mp4'
import { playSound } from '../../utils/AudioPlayer'

const TARGET_SCALE = 0.6
const INITIAL_SCALE = 0.17

const Card = (props) => {
  const pan = useRef(new Animated.ValueXY(props.pos)).current
  const scale = useRef(new Animated.Value(INITIAL_SCALE)).current
  const rotation = useRef(new Animated.Value(props.rot)).current

  const [panEnabled, setPanEnabled] = useState(false)
  const [showCard, setShowCard] = useState(true)

  const [zIndex, setZIndex] = useState(0)

  const getCenterCoordinates = (offsetX, offsetY) => {
    return {
      x: (offsetX + props.target.x),
      y: (offsetY + props.target.y)
    }
  }

  const getTargetCoordinates = (slotX, slotY) => {
    if (slotX === 0 && slotY === 0) { return { x: 0, y: 0 } }
    return {
      x: (slotX - props.target.x + TARGET_SCALE * styles.card.width / 2 + 3),
      y: (slotY - props.target.y + TARGET_SCALE * styles.card.height / 2 + 3)
    }
  }

  function isSlotDraggable (target, slot, color, index) {
    return target.x !== 0 && target.y !== 0 && slot.c.includes(color) && props.usedSlots[index] === false
  }

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: (e, state) => true,
      onMoveShouldSetPanResponder: (e, state) => false,
      onPanResponderGrant: (e, state) => {
        playSound(grab)

        pan.addListener(({ x, y }) => {
          if (isOverlappingTrashCan(x, y)) {
            props.onEnterTrashCan(true)
          } else {
            props.onEnterTrashCan(false)
          }
        })

        setZIndex(1)
        const color = props.color
        props.onBeginDrag(color)
        Animated.timing(scale, {
          toValue: TARGET_SCALE,
          duration: 250,
          useNativeDriver: false
        }).start()
      },
      onPanResponderMove: Animated.event([null, { dx: pan.x, dy: pan.y }], { useNativeDriver: false }),
      onResponderTerminationRequest: (e, state) => true,
      onPanResponderRelease: (e, state) => {
        pan.removeAllListeners()
        const coords = getCenterCoordinates(state.dx, state.dy)

        let target = { x: 0, y: 0 }
        let cardScale = 1.0

        const isTargetTrashCan = isOverlappingTrashCan(coords.x, coords.y)

        if (isTargetTrashCan) {
          const trasshCanCoords = getTrashCanCoords()
          target = getTargetCoordinates(trasshCanCoords.x, trasshCanCoords.y)
          props.onEndDrag(true, -1, 0, 0)
          playSound(trash)
          setShowCard(false)
          props.onDisableInfo()
        } else {
          const index = getOverlappingCardSlotIndex(coords.x, coords.y)
          const slot = getCardSlotData(index)
          target = getTargetCoordinates(slot.x, slot.y)

          const color = props.color
          const co2Reductions = parseInt(props.co2)
          const cashSavings = parseInt(props.cash)

          if (isSlotDraggable(target, slot, color, index)) {
            setPanEnabled(false)
            cardScale = TARGET_SCALE
            props.onEndDrag(true, index, co2Reductions, cashSavings)
            props.onDisableInfo()
          } else {
            target = { x: 0, y: 0 }
            props.onEndDrag(false, -1, 0, 0)
          }
        }

        Animated.parallel([
          Animated.timing(pan, {
            toValue: { x: target.x, y: target.y },
            duration: 500,
            useNativeDriver: false
          }),
          Animated.timing(scale, {
            toValue: cardScale,
            duration: 250,
            useNativeDriver: false
          })
        ]).start(({ finished }) => {
          if (finished && !isTargetTrashCan) {
            setZIndex(0)
            playSound(drop)
          }
        })
      }
    })
  ).current

  const spin = rotation.interpolate({
    inputRange: [0, 1],
    outputRange: ['-20deg', '0deg']
  })

  useEffect(() => {
    // extract initial position to make card deal animations
    // work properly
    // pan.extractOffset()
    if ('onAnimationBegin' in props) {
      setTimeout(() => {
        props.onAnimationBegin()
      }, props.delay)
    }

    if (props.target && props.delay) {
      Animated.sequence([
        Animated.delay(props.delay),
        Animated.parallel([
          Animated.timing(pan, {
            toValue: { x: props.target.x, y: props.target.y },
            duration: props.duration,
            useNativeDriver: false
          }),
          Animated.timing(scale, {
            toValue: 1.0,
            duration: props.duration,
            useNativeDriver: false
          }),
          Animated.timing(rotation, {
            toValue: 1,
            duration: props.duration,
            useNativeDriver: false
          })
        ])
      ]).start(({ finished }) => {
        if (finished) {
          playSound(drop)
          pan.extractOffset()
          setPanEnabled(true)
          props.onAnimationEnd()
        }
      })
    }
  }, [])

  return (
    <>
      { showCard &&
        <Animated.View id={props.id}
          style={[styles.container, {
            zIndex,
            transform: [{ translateX: pan.x }, { translateY: pan.y }, { rotate: spin }, { scale }]
          }]}
          {...panResponder.panHandlers}>
          <Image style={[styles.card, { pointerEvents: panEnabled ? 'auto' : 'none' }]} source={props.src} />
        </Animated.View>
      }
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    pointerEvents: 'box-none',
    position: 'absolute',
    left: 0,
    top: 0,
    transform: 'translateX(300) translateY(400)',
    width: 225,
    height: 199,
    transformOrigin: 'top left'
  },
  card: {
    pointerEvents: 'auto',
    cursor: 'move',
    position: 'absolute',
    top: -199 / 2,
    left: -225 / 2,
    width: 225,
    height: 199
  }
})

export default Card
